import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Trophy, Plus, Pencil, Trash2, X, Crown, Star, Flame, Award,
  Coins, Sparkles, Clock, Gift, Users, ExternalLink, Link as LinkIcon,
  ChevronLeft, Globe, Pin, Radio, MessageSquare, Heart,
} from "lucide-react";

const ICONS = { Trophy, Crown, Star, Flame, Award, Coins, Sparkles, Clock, Gift, Users, Radio, MessageSquare, Heart };

const INTERNAL_METRICS = [
  { key: "manual",      label: "Manual entries",         icon: Pencil,         help: "You add and edit rows yourself. Great for community challenges." },
  { key: "wins",        label: "Game wins",              icon: Trophy,         help: "Auto-updates from on-site game wins. Optionally filter by one game." },
  { key: "tips_gc",     label: "Top tippers",            icon: Coins,          help: "Auto-updates from Gold Coins tipped to this channel." },
  { key: "watch_time",  label: "Watch minutes",          icon: Clock,          help: "Auto-updates from loyal viewer minutes." },
  { key: "chat_count",  label: "Most active chatters",   icon: MessageSquare,  help: "Auto-updates from chat messages sent in this channel." },
  { key: "subs",        label: "Top subscribers",        icon: Heart,          help: "Auto-updates from subscription events." },
  { key: "gifts_given", label: "Top gifters",            icon: Gift,           help: "Auto-updates from gifts given to this channel." },
  { key: "sparks",      label: "Total Sparks (global)",  icon: Sparkles,       help: "Ranks all users by all-time Sparks earned." },
];

export default function ChannelLeaderboards() {
  const { username } = useParams();
  const { user } = useAuth();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);       // "internal" | "external" | null
  const [editing, setEditing] = useState(null);
  const isOwner = user && (user.username === username || user.is_admin);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/channels/${username}/leaderboards`);
      setBoards(r.data || []);
    } catch { setBoards([]); }
    finally { setLoading(false); }
  }, [username]);
  useEffect(() => { load(); }, [load]);

  const internal = boards.filter(b => b.kind !== "external");
  const external = boards.filter(b => b.kind === "external");

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <Link
            to={`/c/${username}`}
            className="text-[11px] sc-mono uppercase tracking-widest text-zinc-500 hover:text-white flex items-center gap-1 mb-2"
            data-testid="lb-back-to-channel"
          >
            <ChevronLeft className="w-3 h-3" /> Back to @{username}
          </Link>
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[color:var(--sc-volt)]" />
            <div className="sc-label" style={{ color: "var(--sc-volt)" }}>{username}&apos;s Leaderboards</div>
          </div>
          <h1 className="sc-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Board Room</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">
            Every board is 100% customizable. On-site boards auto-update the moment viewers engage — no manual scoring.
            External imports link directly to off-site scoreboards like Clash.gg, CSRoll, or Kick challenges.
          </p>
        </div>
        {isOwner && (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => setCreating("internal")}
              className="sc-btn text-sm flex items-center gap-2"
              data-testid="lb-create-internal"
            >
              <Plus className="w-4 h-4" /> New on-site board
            </button>
            <button
              onClick={() => setCreating("external")}
              className="sc-btn-ghost text-sm flex items-center gap-2 border-[color:var(--sc-cyan)] text-[color:var(--sc-cyan)]"
              data-testid="lb-create-external"
            >
              <LinkIcon className="w-4 h-4" /> Import external board
            </button>
          </div>
        )}
      </div>

      {loading && <div className="text-center text-zinc-500 sc-mono py-10">loading…</div>}

      {!loading && boards.length === 0 && (
        <div className="border border-white/10 rounded-md p-12 text-center">
          <Trophy className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
          <h2 className="sc-heading text-xl font-bold mb-2">No leaderboards yet</h2>
          <p className="text-sm text-zinc-500 max-w-md mx-auto">
            {isOwner
              ? "Spin up your first board to spotlight your top viewers, or import your existing scoreboard from another site."
              : "This channel hasn't created any leaderboards yet. Check back soon."}
          </p>
        </div>
      )}

      {/* External boards row */}
      {external.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-4 h-4 text-[color:var(--sc-cyan)]" />
            <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>External Boards</div>
            <span className="text-[10px] text-zinc-600">({external.length})</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {external.map(b => (
              <ExternalCard
                key={b.id}
                board={b}
                isOwner={isOwner}
                onEdit={() => setEditing(b)}
                onDeleted={load}
              />
            ))}
          </div>
        </div>
      )}

      {/* Internal boards row */}
      {internal.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-[color:var(--sc-volt)]" />
            <div className="sc-label" style={{ color: "var(--sc-volt)" }}>On-Site Boards</div>
            <span className="text-[10px] text-zinc-600">({internal.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {internal.map(b => (
              <InternalCard
                key={b.id}
                board={b}
                channelUsername={username}
                isOwner={isOwner}
                onEdit={() => setEditing(b)}
                onReload={load}
              />
            ))}
          </div>
        </div>
      )}

      {(creating || editing) && (
        <BoardEditor
          initial={editing || null}
          defaultKind={creating || editing?.kind || "internal"}
          onClose={() => { setCreating(null); setEditing(null); }}
          onSaved={() => { setCreating(null); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ==========================================================
function ExternalCard({ board, isOwner, onEdit, onDeleted }) {
  const Icon = ICONS[board.icon] || Globe;
  const url = board.external_url || "#";
  let hostname = "";
  try { hostname = new URL(url).hostname.replace(/^www\./, ""); } catch { /* */ }

  const del = async () => {
    if (!confirm(`Delete external board "${board.title}"?`)) return;
    try { await api.delete(`/channels/me/leaderboards/${board.id}`); toast.success("Deleted"); onDeleted(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div
      className="border border-[color:var(--sc-cyan)]/30 rounded-md p-5 hover:border-[color:var(--sc-cyan)]/60 transition group"
      style={{ background: "linear-gradient(180deg, rgba(34,211,238,0.06), transparent 60%)" }}
      data-testid={`external-board-${board.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 rounded-sm border border-[color:var(--sc-cyan)]/40">
          <Icon className="w-5 h-5 text-[color:var(--sc-cyan)]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="sc-heading font-bold text-base truncate">{board.title}</div>
            {board.pinned && <span className="chip-neon volt text-[9px]">PINNED</span>}
          </div>
          {board.description && <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{board.description}</div>}
          {hostname && (
            <div className="text-[10px] sc-mono text-zinc-500 mt-2 truncate">
              {hostname}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="sc-btn text-xs flex items-center gap-2 flex-1 justify-center"
          data-testid={`external-visit-${board.id}`}
        >
          <ExternalLink className="w-3.5 h-3.5" /> Visit
        </a>
        {isOwner && (
          <>
            <button onClick={onEdit} className="sc-btn-ghost text-xs p-2" data-testid={`external-edit-${board.id}`} aria-label="Edit">
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button onClick={del} className="sc-btn-ghost text-xs p-2 text-[color:var(--sc-pink)]" data-testid={`external-delete-${board.id}`} aria-label="Delete">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ==========================================================
function InternalCard({ board, channelUsername, isOwner, onEdit, onReload }) {
  const Icon = ICONS[board.icon] || Trophy;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingEntry, setAddingEntry] = useState(false);
  const [recentPayouts, setRecentPayouts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/channels/${channelUsername}/leaderboards/${board.id}`);
      setEntries(r.data.entries || []);
      setRecentPayouts(r.data.recent_payouts || []);
    } catch { setEntries([]); setRecentPayouts([]); }
    finally { setLoading(false); }
  }, [channelUsername, board.id]);
  useEffect(() => { load(); }, [load]);

  const deleteBoard = async () => {
    if (!confirm(`Delete "${board.title}"? All entries will be lost.`)) return;
    try { await api.delete(`/channels/me/leaderboards/${board.id}`); toast.success("Deleted"); onReload(); }
    catch { toast.error("Failed"); }
  };
  const removeEntry = async (u) => {
    if (!confirm(`Remove @${u}?`)) return;
    try { await api.delete(`/channels/me/leaderboards/${board.id}/entries/${encodeURIComponent(u)}`); load(); }
    catch { toast.error("Failed"); }
  };

  const metricMeta = INTERNAL_METRICS.find(m => m.key === board.metric);
  const autoBadge = board.metric !== "manual"
    ? { label: "AUTO", color: "var(--sc-volt)" }
    : { label: "MANUAL", color: "var(--sc-cyan)" };

  return (
    <div className="border border-white/10 rounded-md p-5" data-testid={`internal-board-${board.id}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 rounded-sm border border-white/10">
          <Icon className="w-5 h-5 text-[color:var(--sc-volt)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="sc-heading font-bold text-base truncate">{board.title}</div>
            <span
              className="text-[9px] sc-mono tracking-widest px-1.5 py-0.5 rounded-sm border"
              style={{ borderColor: `${autoBadge.color}55`, color: autoBadge.color }}
            >
              {autoBadge.label}
            </span>
            {board.pinned && <Pin className="w-3 h-3 text-[color:var(--sc-volt)]" />}
          </div>
          {board.description && <div className="text-xs text-zinc-400 mt-1">{board.description}</div>}
          {metricMeta && (
            <div className="text-[10px] sc-mono text-zinc-500 mt-1">
              Tracking: {metricMeta.label.toLowerCase()}
              {board.game_slug ? ` · ${board.game_slug}` : ""}
            </div>
          )}
          {(board.payout_prizes || []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`prizes-${board.id}`}>
              {board.payout_prizes.map((p, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-[10px] sc-mono px-1.5 py-0.5 rounded-sm border border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/[0.06]"
                >
                  <span className="text-[color:var(--sc-volt)] font-bold">{i === 0 ? "1st" : i === 1 ? "2nd" : "3rd"}</span>
                  {p.gold_coins > 0 && <span className="text-[#FFD700]">{p.gold_coins} GC</span>}
                  {p.sparks > 0 && <span className="text-[color:var(--sc-cyan)] inline-flex items-center gap-0.5">{p.sparks} <Sparkles className="w-3 h-3" /></span>}
                </span>
              ))}
              <span className="text-[9px] text-zinc-500 sc-mono ml-1 self-center">/ month</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-1">
        {loading && <div className="text-[11px] text-zinc-500 sc-mono py-3">loading…</div>}
        {!loading && entries.length === 0 && (
          <div className="text-[12px] text-zinc-500 py-3">
            {board.metric === "manual"
              ? (isOwner ? "No entries yet — add the first row below." : "No entries yet.")
              : "No activity yet. This board updates automatically as viewers engage."}
          </div>
        )}
        {entries.map((e, i) => (
          <div key={`${e.username}-${i}`} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/5 last:border-0" data-testid={`row-${board.id}-${e.username}`}>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className={`sc-mono text-[11px] w-5 text-right flex-shrink-0 ${
                i === 0 ? "text-[color:var(--sc-volt)]" : i === 1 ? "text-[color:var(--sc-cyan)]" : i === 2 ? "text-[color:var(--sc-orange)]" : "text-zinc-600"
              }`}>{i + 1}</span>
              <Link to={`/c/${e.username}`} className="text-sm text-white truncate hover:text-[color:var(--sc-volt)]">@{e.username}</Link>
              {e.notes && <span className="text-[10px] text-zinc-500 truncate">· {e.notes}</span>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="sc-mono text-sm font-bold text-[color:var(--sc-volt)]">
                {typeof e.value === "number" ? e.value.toLocaleString() : e.value}
              </span>
              <span className="text-[10px] text-zinc-500">{board.unit_label}</span>
              {isOwner && board.metric === "manual" && (
                <button onClick={() => removeEntry(e.username)} className="text-zinc-500 hover:text-red-400" aria-label="Remove" data-testid={`row-remove-${board.id}-${e.username}`}>
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {recentPayouts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5" data-testid={`payouts-${board.id}`}>
          <div className="sc-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 flex items-center gap-1">
            <Gift className="w-3 h-3" /> Last payout · {recentPayouts[0].month}
            {recentPayouts[0].status === "underfunded" && (
              <span className="text-[color:var(--sc-orange)] ml-1">(underfunded — skipped)</span>
            )}
          </div>
          {recentPayouts[0].winners.length === 0 && recentPayouts[0].status === "paid" && (
            <div className="text-[11px] text-zinc-500">No qualifying activity that cycle.</div>
          )}
          {recentPayouts[0].winners.slice(0, 3).map(w => (
            <div key={w.place} className="text-[11px] text-zinc-300 flex items-center gap-2">
              <span className="sc-mono text-[10px] text-[color:var(--sc-volt)] w-6">{w.place === 1 ? "1st" : w.place === 2 ? "2nd" : "3rd"}</span>
              <Link to={`/c/${w.username}`} className="font-bold hover:text-[color:var(--sc-volt)]">@{w.username}</Link>
              <span className="text-zinc-500 flex-1 text-right sc-mono">
                {w.gold_coins > 0 && <span className="text-[#FFD700]">+{w.gold_coins} GC</span>}
                {w.gold_coins > 0 && w.sparks > 0 && <span className="mx-1">·</span>}
                {w.sparks > 0 && <span className="text-[color:var(--sc-cyan)] inline-flex items-center gap-0.5">+{w.sparks} <Sparkles className="w-3 h-3" /></span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {isOwner && board.metric === "manual" && (
        addingEntry ? (
          <ManualEntryForm boardId={board.id} unitLabel={board.unit_label} onDone={() => { setAddingEntry(false); load(); }} onCancel={() => setAddingEntry(false)} />
        ) : (
          <button onClick={() => setAddingEntry(true)} className="sc-btn-ghost text-[11px] mt-3 flex items-center gap-1" data-testid={`add-entry-${board.id}`}>
            <Plus className="w-3 h-3" /> Add manual entry
          </button>
        )
      )}

      {isOwner && (
        <div className="flex items-center gap-2 pt-3 mt-3 border-t border-white/5">
          <button onClick={onEdit} className="sc-btn-ghost text-[11px] flex items-center gap-1" data-testid={`internal-edit-${board.id}`}>
            <Pencil className="w-3 h-3" /> Edit
          </button>
          <button onClick={deleteBoard} className="sc-btn-ghost text-[11px] flex items-center gap-1 text-[color:var(--sc-pink)]" data-testid={`internal-delete-${board.id}`}>
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ==========================================================
function ManualEntryForm({ boardId, unitLabel, onDone, onCancel }) {
  const [username, setUsername] = useState("");
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !value) return toast.error("Username + value required");
    setBusy(true);
    try {
      await api.post(`/channels/me/leaderboards/${boardId}/entries`, {
        username: username.trim(), value: parseFloat(value), notes,
      });
      toast.success("Entry saved");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-[1fr_90px_auto] gap-1 mt-3 items-center">
      <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="username" className="sc-input py-1 text-xs" required data-testid="manual-username" />
      <input value={value} onChange={(e) => setValue(e.target.value)} type="number" step="any" placeholder={unitLabel} className="sc-input py-1 text-xs" required data-testid="manual-value" />
      <div className="flex gap-1">
        <button type="submit" disabled={busy} className="sc-btn text-[10px] py-1 px-2" data-testid="manual-save">Save</button>
        <button type="button" onClick={onCancel} className="sc-btn-ghost text-[10px] py-1 px-2">✕</button>
      </div>
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="notes (optional)" className="sc-input py-1 text-xs col-span-3" data-testid="manual-notes" />
    </form>
  );
}

// ==========================================================
function BoardEditor({ initial, defaultKind, onClose, onSaved }) {
  const [kind, setKind] = useState(initial?.kind || defaultKind || "internal");
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [metric, setMetric] = useState(initial?.metric || "manual");
  const [unitLabel, setUnitLabel] = useState(initial?.unit_label || "pts");
  const [icon, setIcon] = useState(initial?.icon || "Trophy");
  const [sortOrder, setSortOrder] = useState(initial?.sort_order || "desc");
  const [gameSlug, setGameSlug] = useState(initial?.game_slug || "");
  const [externalUrl, setExternalUrl] = useState(initial?.external_url || "");
  const [limit, setLimit] = useState(initial?.limit || 25);
  const [pinned, setPinned] = useState(!!initial?.pinned);
  const [prizes, setPrizes] = useState(() => {
    const base = [{ gold_coins: 0, sparks: 0 }, { gold_coins: 0, sparks: 0 }, { gold_coins: 0, sparks: 0 }];
    (initial?.payout_prizes || []).forEach((p, i) => { if (i < 3) base[i] = { gold_coins: Number(p.gold_coins || 0), sparks: Number(p.sparks || 0) }; });
    return base;
  });
  const [busy, setBusy] = useState(false);

  const updatePrize = (idx, field, val) => {
    const next = prizes.map((p, i) => i === idx ? { ...p, [field]: Math.max(0, parseInt(val, 10) || 0) } : p);
    setPrizes(next);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title required");
    if (kind === "external" && !externalUrl.trim()) return toast.error("Please enter the external URL");
    setBusy(true);
    try {
      // Trim trailing empty prize slots (0/0)
      const trimmedPrizes = [...prizes];
      while (trimmedPrizes.length && trimmedPrizes[trimmedPrizes.length - 1].gold_coins === 0 && trimmedPrizes[trimmedPrizes.length - 1].sparks === 0) {
        trimmedPrizes.pop();
      }
      const base = {
        title: title.trim(),
        description: description.trim(),
        kind,
        icon,
        pinned,
      };
      const payload = kind === "external"
        ? { ...base, external_url: externalUrl.trim(), metric: "manual", unit_label: "pts", sort_order: "desc", limit: 3, game_slug: null, payout_prizes: [] }
        : {
            ...base,
            metric,
            unit_label: unitLabel,
            sort_order: sortOrder,
            game_slug: (metric === "wins" ? (gameSlug.trim() || null) : null),
            limit: Math.max(3, Math.min(100, parseInt(limit, 10) || 25)),
            external_url: null,
            payout_prizes: trimmedPrizes,
          };
      if (initial) {
        await api.patch(`/channels/me/leaderboards/${initial.id}`, payload);
      } else {
        await api.post("/channels/me/leaderboards", payload);
      }
      toast.success(initial ? "Updated" : "Created");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(20px)" }} onClick={onClose}>
      <form onSubmit={save} className="sc-card p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="board-editor">
        <div className="flex items-center justify-between mb-4">
          <div className="sc-heading text-lg font-black">{initial ? "Edit leaderboard" : (kind === "external" ? "Import external leaderboard" : "New on-site leaderboard")}</div>
          <button type="button" onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        {/* Kind toggle */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            type="button"
            onClick={() => setKind("internal")}
            data-testid="kind-internal"
            className={`p-3 rounded-sm border text-left transition ${kind === "internal" ? "border-[color:var(--sc-volt)] bg-[color:var(--sc-volt)]/10" : "border-white/10 hover:border-white/30"}`}
          >
            <Trophy className="w-4 h-4 mb-1 text-[color:var(--sc-volt)]" />
            <div className="text-xs font-bold">On-site (auto)</div>
            <div className="text-[10px] text-zinc-500 mt-1">Auto-updates from viewer activity.</div>
          </button>
          <button
            type="button"
            onClick={() => setKind("external")}
            data-testid="kind-external"
            className={`p-3 rounded-sm border text-left transition ${kind === "external" ? "border-[color:var(--sc-cyan)] bg-[color:var(--sc-cyan)]/10" : "border-white/10 hover:border-white/30"}`}
          >
            <LinkIcon className="w-4 h-4 mb-1 text-[color:var(--sc-cyan)]" />
            <div className="text-xs font-bold">External import</div>
            <div className="text-[10px] text-zinc-500 mt-1">Link to Clash.gg, CSRoll, Kick, etc.</div>
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="sc-label block mb-1">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} className="sc-input text-sm" required data-testid="editor-title" placeholder={kind === "external" ? "e.g. Clash.gg Wager Race" : "e.g. Loyal viewers of the month"} />
          </div>
          <div>
            <label className="sc-label block mb-1">Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={400} className="sc-input text-sm" data-testid="editor-description" />
          </div>

          {kind === "external" ? (
            <div>
              <label className="sc-label block mb-1">External URL</label>
              <input
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                type="url"
                placeholder="https://clash.gg/r/yourcode"
                className="sc-input text-sm"
                required
                data-testid="editor-external-url"
              />
              <div className="text-[10px] text-zinc-500 mt-1">
                Viewers will see a &quot;Visit&quot; button that opens this URL in a new tab. Great for wager races, affiliate leaderboards, and third-party challenges.
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="sc-label block mb-1">What is this ranked by?</label>
                <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                  {INTERNAL_METRICS.map((m) => (
                    <label
                      key={m.key}
                      className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition ${metric === m.key ? "border-[color:var(--sc-volt)] bg-[color:var(--sc-volt)]/10" : "border-white/10 hover:border-white/30"}`}
                      data-testid={`metric-${m.key}`}
                    >
                      <input type="radio" name="metric" value={m.key} checked={metric === m.key} onChange={() => setMetric(m.key)} className="mt-0.5" />
                      <m.icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[color:var(--sc-volt)]" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold text-white">{m.label}</div>
                        <div className="text-[10px] text-zinc-500 leading-relaxed">{m.help}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              {metric === "wins" && (
                <div>
                  <label className="sc-label block mb-1">Filter by game (optional)</label>
                  <input value={gameSlug} onChange={(e) => setGameSlug(e.target.value)} placeholder="e.g. chess, holdem, slots" className="sc-input text-sm" data-testid="editor-game-slug" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="sc-label block mb-1">Unit label</label>
                  <input value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} maxLength={20} className="sc-input text-sm" data-testid="editor-unit" />
                </div>
                <div>
                  <label className="sc-label block mb-1">Show top…</label>
                  <input value={limit} onChange={(e) => setLimit(e.target.value)} type="number" min={3} max={100} className="sc-input text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="sc-label block mb-1">Sort</label>
                  <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="sc-input text-sm">
                    <option value="desc">Highest first</option>
                    <option value="asc">Lowest first</option>
                  </select>
                </div>
                <div>
                  <label className="sc-label block mb-1">Icon</label>
                  <select value={icon} onChange={(e) => setIcon(e.target.value)} className="sc-input text-sm" data-testid="editor-icon">
                    {Object.keys(ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Monthly automated payouts — internal boards only */}
          {kind === "internal" && (
            <div className="border border-[color:var(--sc-volt)]/25 rounded-sm p-3 bg-[color:var(--sc-volt)]/[0.03]" data-testid="editor-prizes">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="w-3.5 h-3.5 text-[color:var(--sc-volt)]" />
                <div className="sc-label" style={{ color: "var(--sc-volt)" }}>Monthly Auto-Payouts</div>
              </div>
              <div className="text-[10px] text-zinc-500 mb-3">
                Set up to 3 monthly prizes. Winners are chosen by highest activity delta each UTC month.
                <span className="text-zinc-400"> Prizes are paid from your balance</span> — if you&apos;re short at settlement, the payout is skipped and logged as underfunded.
              </div>
              {[0, 1, 2].map((idx) => {
                const label = idx === 0 ? "1st place" : idx === 1 ? "2nd place" : "3rd place";
                return (
                  <div key={idx} className="grid grid-cols-[80px_1fr_1fr] gap-2 items-center mb-2" data-testid={`prize-row-${idx + 1}`}>
                    <div className="sc-mono text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
                    <div className="flex items-center gap-1">
                      <Coins className="w-3 h-3 text-[#FFD700]" />
                      <input
                        type="number" min={0}
                        value={prizes[idx].gold_coins}
                        onChange={(e) => updatePrize(idx, "gold_coins", e.target.value)}
                        className="sc-input py-1 text-xs flex-1"
                        placeholder="Gold Coins"
                        data-testid={`prize-gc-${idx + 1}`}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-[color:var(--sc-cyan)]" />
                      <input
                        type="number" min={0}
                        value={prizes[idx].sparks}
                        onChange={(e) => updatePrize(idx, "sparks", e.target.value)}
                        className="sc-input py-1 text-xs flex-1"
                        placeholder="Sparks"
                        data-testid={`prize-sparks-${idx + 1}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="editor-pinned" />
            Pin to top
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="sc-btn-ghost text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="sc-btn text-xs" data-testid="editor-save">
            {busy ? "Saving…" : initial ? "Save changes" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}
